import { Component, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormContainerComponent } from '../../../../ui-shared/form-container/form-container.component';
import { FormErrorService } from '../../../../services/form-error.service';
import { HasPermissionDirective } from '../../../../directives/has-permission.directive';
import { Resources, Scopes } from '../../../../models/settlement-permissions.model';
import { TextInputComponent } from '../../../../ui-shared/text-input/text-input.component';
import { ValidationService } from '../../../../services/validation.service';
import type { WalletCheckResponse } from '../../../../services/merchant.service';
import { WalletService } from '../../../../services/wallet.service';
import { LoadingSpinnerComponent } from '../../../../ui-shared/loading-spinner/loading-spinner.component';
import { MatIcon } from '@angular/material/icon';
import { NetworkMetadataService } from '@olv/ui-shared/services/network-metadata.service';

@Component({
  selector: 'olv-wallet-check-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    FormContainerComponent,
    MatProgressSpinnerModule,
    TextInputComponent,
    HasPermissionDirective,
    LoadingSpinnerComponent,
  ],
  templateUrl: './wallet-check-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./wallet-check-form.component.scss'],
})
export class WalletCheckFormComponent {
  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;

  private formErrorService = inject(FormErrorService);
  private walletService = inject(WalletService);
  private readonly metadata = inject(NetworkMetadataService);

  merchantId = input.required<string>();

  isValidWallet = signal(false);
  hasResult = signal(false);
  isLoading = signal(false);
  errorMessage = signal('');

  walletCheckForm = new FormGroup({
    walletAddress: new FormControl('', [Validators.required, ValidationService.addressValidator(() => this.metadata.networks())]),
  });

  constructor() {
    this.metadata
      .whenLoaded()
      .subscribe(() => this.walletCheckForm.get('walletAddress')?.updateValueAndValidity({ emitEvent: false }));
    this.walletCheckForm.get('walletAddress')?.valueChanges.subscribe(() => {
      this.resetState();
    });
  }

  getFieldError(controlName: string, fieldName: string, customMessages?: { [key: string]: string }): string {
    return this.formErrorService.getErrorMessage(this.walletCheckForm.get(controlName), fieldName, customMessages);
  }

  onSubmit(): void {
    if (this.walletCheckForm.invalid || this.isLoading()) {
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set('');

    const walletAddress = (this.walletCheckForm.value.walletAddress as string).trim();

    this.walletService.checkMerchantWallet(walletAddress).subscribe({
      next: (result: WalletCheckResponse) => {
        this.isValidWallet.set(result.isOwner);
        this.hasResult.set(true);
        this.isLoading.set(false);
      },

      error: (error) => {
        this.errorMessage.set(error.message);
        this.isLoading.set(false);
      },
    });
  }

  onReset(): void {
    this.walletCheckForm.reset();
    this.resetState();
  }

  resetState(): void {
    this.hasResult.set(false);
    this.isValidWallet.set(false);
    this.errorMessage.set('');
  }

  getMessageResult(): string {
    return this.isValidWallet()
      ? 'This wallet belongs to this merchant'
      : 'This wallet does not belong to this merchant';
  }
}
